// <pos-dashboard/> — 儀表板
// Owner 看營收概覽 + 今日建議推銷；Cashier 直接跳 /pos
// see architecture.md#L580, #L1115（效期驅動 pipeline）

import { fetchTodayRecommendations } from '../lib/api.js';
import { getSupabase } from '../lib/supabase-client.js';

// 台北今日 00:00 的 UTC 時刻（同 src/agents/tools.js taipeiTodayMidnightUTC 邏輯）
const TPE_OFFSET_MS = 8 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
function taipeiTodayStartUtcMs() {
  const tpe = new Date(Date.now() + TPE_OFFSET_MS);
  return Date.UTC(tpe.getUTCFullYear(), tpe.getUTCMonth(), tpe.getUTCDate()) - TPE_OFFSET_MS;
}

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const URGENCY_LABEL = Object.freeze({
  high: '🔥 急推',
  medium: '推薦',
  low: '次推',
});

const URGENCY_CLASS = Object.freeze({
  high: 'urgency-high',
  medium: 'urgency-medium',
  low: 'urgency-low',
});

class PosDashboard extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.recommendations = null;   // 等於 null 表示尚未載入；空陣列表示載入完成沒推薦
    this.recommendationsLoading = true;
    this.briefing = null;          // 今日小掌櫃晨報（daily_briefings；null = 無/未載）
    this.stats = null;             // { revenue, orderCount, topItem } | null=載入中/失敗顯示 —
  }

  connectedCallback() {
    // Codex review: pos-auth-gate 保證在所有 guarded 路由 mount 前已 hydrate
    // body.dataset.role（見 src/main.js applySessionToBody + auth:changed event）。
    // 這裡讀 dataset 是安全的；若未來 auth-gate 改成 lazy hydration 需重新審視。
    const role = document.body.dataset.role || 'cashier';
    if (role === 'cashier') {
      location.hash = '#/pos';
      return;
    }
    this.render();
    this.loadRecommendations();
    this.loadStats();
    this.loadBriefing();
  }

  /** 今日小掌櫃晨報（xiaobrief-batch 06:00 產出；RLS 同 tenant 可讀）。fail-soft。 */
  async loadBriefing() {
    try {
      const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Taipei' }).format(new Date());
      const { data } = await getSupabase().from('daily_briefings')
        .select('content, generated_by').eq('for_date', today).maybeSingle();
      this.briefing = data || null;
    } catch { this.briefing = null; }
    this.render();
  }

  /** 今日營收/單數 + 本週 top 品項。全程 fail-soft：任何失敗維持「—」不擋頁面 */
  async loadStats() {
    try {
      const supabase = getSupabase();
      const todayStart = new Date(taipeiTodayStartUtcMs()).toISOString();
      const weekStart = new Date(taipeiTodayStartUtcMs() - 6 * DAY_MS).toISOString();

      const { data: todayOrders, error: oErr } = await supabase.from('orders')
        .select('total')
        .gte('created_at', todayStart)
        .not('status', 'in', '(cancelled,refunded)')
        .limit(5000);
      if (oErr) throw oErr;

      // 本週 top：近 7 天訂單 → order_items 按 qty 聚合
      const { data: weekOrders } = await supabase.from('orders')
        .select('id')
        .gte('created_at', weekStart)
        .not('status', 'in', '(cancelled,refunded)')
        .limit(5000);
      let topItem = null;
      if (weekOrders?.length) {
        const { data: items } = await supabase.from('order_items')
          .select('qty, menu_item:menu_items(name)')
          .in('order_id', weekOrders.map((o) => o.id))
          .limit(10000);
        const byName = new Map();
        for (const it of items || []) {
          const name = it.menu_item?.name;
          if (name) byName.set(name, (byName.get(name) || 0) + Number(it.qty || 0));
        }
        const top = [...byName.entries()].sort((a, b) => b[1] - a[1])[0];
        if (top) topItem = { name: top[0], qty: top[1] };
      }

      this.stats = {
        revenue: Math.round((todayOrders || []).reduce((s, o) => s + Number(o.total || 0), 0)),
        orderCount: (todayOrders || []).length,
        topItem,
      };
    } catch {
      this.stats = null;  // 維持 — （離線 / 測試環境 / 查詢失敗）
    }
    this.render();
  }

  async loadRecommendations() {
    this.recommendationsLoading = true;
    try {
      const data = await fetchTodayRecommendations();
      this.recommendations = data;
    } catch {
      this.recommendations = { items: [], item_count: 0 };
    } finally {
      this.recommendationsLoading = false;
      this.render();
    }
  }

  renderRecommendationsWidget() {
    if (this.recommendationsLoading) {
      return `<div class="rec-empty">載入中…</div>`;
    }
    const items = this.recommendations?.items || [];
    if (items.length === 0) {
      return `
        <div class="rec-empty">
          今日尚無推薦清單<br/>
          <span class="rec-empty__hint">小掌櫃每天凌晨自動幫你看食材效期，設定庫存後隔天就會出現建議。</span>
        </div>
      `;
    }
    return `
      <ul class="rec-list">
        ${items.slice(0, 5).map((it) => {
          const cls = URGENCY_CLASS[it.urgency] || URGENCY_CLASS.medium;
          const label = URGENCY_LABEL[it.urgency] || URGENCY_LABEL.medium;
          const reason = it.reason ? `<div class="rec-reason">${escapeHtml(it.reason)}</div>` : '';
          return `
            <li class="rec-item ${cls}">
              <div class="rec-item__head">
                <span class="rec-badge">${escapeHtml(label)}</span>
                <span class="rec-name">${escapeHtml(it.name)}</span>
                <span class="rec-price">NT$ ${it.price != null ? escapeHtml(it.price) : '—'}</span>
              </div>
              ${reason}
            </li>
          `;
        }).join('')}
      </ul>
    `;
  }

  render() {
    if (!this.isConnected) return;

    this.shadowRoot.innerHTML = `
      <style>
        :host { display: block; padding: var(--s-5); }
        .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: var(--s-4); margin-bottom: var(--s-5); }
        .stat { padding: var(--s-5); background: var(--bg-elevated); border-radius: var(--r-lg); }
        .stat h3 { color: var(--fg-muted); font-size: 14px; margin-bottom: var(--s-2); }
        .stat .v { font-size: 28px; font-weight: 600; color: var(--brand-primary); }

        .rec-card {
          padding: var(--s-5);
          background: var(--bg-elevated);
          border-radius: var(--r-lg);
          border-left: 4px solid var(--brand-primary);
        }
        .rec-card h3 {
          font-size: 16px;
          color: var(--brand-primary);
          margin: 0 0 var(--s-4);
          display: flex;
          align-items: center;
          gap: var(--s-2);
        }
        .rec-card h3 .meta { font-size: 12px; color: var(--fg-muted); font-weight: normal; margin-left: auto; }
        .rec-empty {
          text-align: center;
          color: var(--fg-muted);
          padding: var(--s-5) 0;
          line-height: 1.6;
        }
        .rec-empty__hint { font-size: 12px; color: var(--fg-dim); }
        .rec-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: var(--s-3); }
        .rec-item {
          padding: var(--s-3) var(--s-4);
          border-radius: var(--r-md);
          background: var(--bg-base);
          border: 1px solid var(--border);
        }
        .rec-item.urgency-high { border-color: #dc2626; background: rgba(220,38,38,.05); }
        .rec-item.urgency-medium { border-color: #f59e0b; background: rgba(245,158,11,.05); }
        .rec-item.urgency-low { background: var(--bg-base); }
        .rec-item__head { display: flex; align-items: center; gap: var(--s-3); flex-wrap: wrap; }
        .rec-badge {
          font-size: 12px;
          font-weight: 700;
          padding: 2px 8px;
          border-radius: var(--r-sm);
          background: var(--bg-elevated);
        }
        .urgency-high .rec-badge { background: #dc2626; color: white; }
        .urgency-medium .rec-badge { background: #f59e0b; color: white; }
        .rec-name { font-weight: 600; font-size: 15px; }
        .rec-price { color: var(--fg-muted); margin-left: auto; font-family: var(--font-mono); }
        .rec-reason {
          margin-top: var(--s-2);
          font-size: 13px;
          color: var(--fg-muted);
          line-height: 1.5;
        }
      </style>

      <div class="grid">
        <div class="stat"><h3>今日營收</h3>
          <div class="v" data-testid="stat-revenue">NT$ ${this.stats ? this.stats.revenue.toLocaleString('zh-TW') : '—'}</div></div>
        <div class="stat"><h3>今日訂單數</h3>
          <div class="v" data-testid="stat-orders">${this.stats ? this.stats.orderCount : '—'}</div></div>
        <div class="stat"><h3>本週 top 品項</h3>
          <div class="v" data-testid="stat-top" style="font-size:20px;">${this.stats?.topItem ? `${escapeHtml(this.stats.topItem.name)} ×${this.stats.topItem.qty}` : '—'}</div></div>
        <div class="stat"><h3>小掌櫃建議</h3><div class="v" style="font-size:16px;color:var(--fg-muted);">→ 下方</div></div>
      </div>

      <pos-mascot-intro></pos-mascot-intro>
      <pos-onboarding-checklist></pos-onboarding-checklist>

      ${this.briefing ? `
      <div class="rec-card">
        <h3>☀️ 小掌櫃晨報 <span class="meta">${this.briefing.generated_by === 'fallback' ? '（數據版）' : ''}</span></h3>
        <p style="line-height:1.8;margin:0;">${escapeHtml(this.briefing.content)}</p>
      </div>` : ''}

      ${(this.recommendationsLoading || (this.recommendations?.items || []).length) ? `
      <div class="rec-card">
        <h3>
          🔥 今日建議推銷
          <span class="meta">${this.recommendations?.for_date ? escapeHtml(this.recommendations.for_date) : ''}</span>
        </h3>
        ${this.renderRecommendationsWidget()}
      </div>` : ''}
    `;
  }
}

customElements.define('pos-dashboard', PosDashboard);
